<template>
    <div :class="[
        'requests-page requests-glass argus-glass-page',
        {
            'requests-page--telegram': isTelegramWebApps,
            'requests-page--mobile-layout': isMobileBrowser
        }
    ]"
         @touchstart.passive="onTabSwipeStart"
         @touchend.passive="onTabSwipeEnd"
         @touchcancel.passive="resetTabSwipe">
        <RequestsNavbar
                        :sortOptions="sortOptions"
                        :sortBy="sortBy"
                        :sortDesc="sortDesc"
                        :filter="filter"
                        :statusFilter="statusFilter"
                        :requestTypeFilter="requestTypeFilter"
                        :requestTypes="requestTypeFilterOptions"
                        :buildingProcessFilter="buildingProcessFilter"
                        :buildingProcessOptions="buildingProcessFilterOptions"
                        :badgeFilterOptions="badgeFilterOptions"
                        :recurrenceFilter="$store.state.requests.recurrenceFilter"
                        @recurrenceFilterChange="changeRecurrenceFilter"
                        @sortByChange="onSortByChange"
                        @sortDescChange="onSortDescChange"
                        @filterChange="onFilterChange"
                        @statusFilterChange="onStatusFilterChange"
                        @requestTypeFilterChange="onRequestTypeFilterChange"
                        @buildingProcessFilterChange="onBuildingProcessFilterChange"
                        @filtersReset="onFiltersReset"
                        @layoutChange="scheduleUpdateScrollHeight"
                        :canCreateRequest="canCreateRequest"
        />

        <b-card no-body class="requests-list-card">
            <transition name="requests-swipe-hint">
                <div v-if="showTabSwipeHint" class="requests-swipe-hint" role="status">
                    <i class="fas fa-arrows-alt-h requests-swipe-hint__icon" aria-hidden="true"></i>
                    <span>Листайте список влево или вправо, чтобы переключать вкладки</span>
                    <button
                        type="button"
                        class="requests-swipe-hint__close"
                        aria-label="Закрыть подсказку"
                        @click="dismissTabSwipeHint"
                    >
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </div>
            </transition>
            <b-tabs card class="requests-tabs" v-model="selectedTab" @input="onTabChange">
                <b-tab ref="myRequests">
                    <template #title>
                        <span class="d-none d-md-inline">Я создал</span>
                        <span class="d-md-none">Мои</span>
                        <b-badge v-if="counts.my > 0" variant="secondary" class="ml-1">
                            {{ counts.my }}
                        </b-badge>
                    </template>
                    <div ref="scrollBody" class="requests-scroll-body">
                        <div class="d-flex justify-content-center py-3">
                            <span class="text-success" v-if="!allRequests.length && !onProcess">
                                Вы не создали ни одной заявки
                            </span>
                            <b-spinner label="Loading..." v-if="onProcess"></b-spinner>
                        </div>
                        <RequestShortItem v-for="request in allRequests" :request="request" :key="request.id"
                                          :id="`id_${request.id}`"
                                          :ref="`ref_${request.id}`"
                                          @scrollToMe="scrollToElement"
                                          @layoutChanged="scheduleUpdateScrollHeight"/>
                    </div>
                </b-tab>

                <b-tab ref="requiresActionRequests">
                    <template #title>
                        <span class="d-none d-md-inline">Требуется мое участие</span>
                        <span class="d-md-none">Участие</span>
                        <b-badge v-if="counts.requires_action > 0" variant="danger" class="ml-1">
                            {{ counts.requires_action }}
                        </b-badge>
                    </template>
                    <div class="requests-scroll-body">
                        <div class="d-flex justify-content-center py-3">
                            <span class="text-success" v-if="!allRequests.length && !onProcess">
                                Нет заявок, требующих вашего участия
                            </span>
                            <b-spinner label="Loading..." v-if="onProcess"></b-spinner>
                        </div>
                        <RequestShortItem v-for="request in allRequests" :request="request" :key="request.id"
                                          :id="`id_${request.id}`"
                                          :ref="`ref_${request.id}`"
                                          @scrollToMe="scrollToElement"
                                          @layoutChanged="scheduleUpdateScrollHeight"/>
                    </div>
                </b-tab>

                <b-tab ref="allRequests">
                    <template #title>
                        Все
                        <b-badge v-if="counts.all > 0" variant="secondary" class="ml-1">
                            {{ counts.all }}
                        </b-badge>
                    </template>
                    <div class="requests-scroll-body">
                        <div class="d-flex justify-content-center py-3">
                            <span class="text-success" v-if="!allRequests.length && !onProcess">
                                Нет доступных заявок
                            </span>
                            <b-spinner label="Loading..." v-if="onProcess"></b-spinner>
                        </div>
                        <RequestShortItem v-for="request in allRequests" :request="request" :key="request.id"
                                          :id="`id_${request.id}`"
                                          :ref="`ref_${request.id}`"
                                          @scrollToMe="scrollToElement"
                                          @layoutChanged="scheduleUpdateScrollHeight"/>
                    </div>
                </b-tab>

                <b-tab v-if="isAdmin" ref="allEmployeesRequests">
                    <template #title>
                        <span class="d-none d-md-inline">Все сотрудники</span>
                        <span class="d-md-none">Сотрудники</span>
                        <b-badge v-if="counts.all_employees > 0" variant="secondary" class="ml-1">
                            {{ counts.all_employees }}
                        </b-badge>
                    </template>
                    <div class="requests-scroll-body">
                        <div class="d-flex justify-content-center py-3">
                            <span class="text-success" v-if="!allRequests.length && !onProcess">
                                Нет доступных заявок сотрудников
                            </span>
                            <b-spinner label="Loading..." v-if="onProcess"></b-spinner>
                        </div>
                        <RequestShortItem v-for="request in allRequests" :request="request" :key="request.id"
                                          :id="`id_${request.id}`"
                                          :ref="`ref_${request.id}`"
                                          @scrollToMe="scrollToElement"
                                          @layoutChanged="scheduleUpdateScrollHeight"/>
                    </div>
                </b-tab>
            </b-tabs>

            <div v-if="listTotal > listPerPage" class="d-flex justify-content-center px-2 py-1 border-top requests-pagination">
                <!-- Мобильная компактная пагинация -->
                <b-pagination
                    v-model="currentPage"
                    :total-rows="listTotal"
                    :per-page="listPerPage"
                    size="sm"
                    :limit="isMobile ? 3 : 7"
                    :first-number="!isMobile"
                    :last-number="!isMobile"
                    @change="onPageChange"
                />
            </div>
        </b-card>
    </div>
</template>

<script>
import RequestsNavbar from "@/views/Requests/RequestsNavbar/RequestsNavbar";
import RequestShortItem from "@/views/Requests/RequestShortItem/RequestShortItem";
import requestsApi from "@/store/Requests/api";
import {REQUEST_CATEGORIES} from "@/constants/requestCategories";

const BASE_TAB_NAMES = ['my', 'requires_action', 'all'];
const ADMIN_TAB_NAMES = BASE_TAB_NAMES.concat(['all_employees']);
const SESSION_KEY = 'requestsListReturn';
const SESSION_TTL_MS = 60 * 60 * 1000; // 1 час
const HIGHLIGHT_CLEANUP_DELAY_MS = 1200;
const REQUEST_CATEGORY_FILTER_PREFIX = 'category:';
const TAB_SWIPE_HINT_STORAGE_KEY = 'requestsTabSwipeHintSeen.v1';
const TAB_SWIPE_HINT_DURATION_MS = 7000;
const TAB_SWIPE_MIN_DISTANCE_PX = 56;
const TAB_SWIPE_MAX_DISTANCE_PX = 88;
const TAB_SWIPE_VIEWPORT_RATIO = 0.16;
const TAB_SWIPE_HORIZONTAL_RATIO = 1.35;
const TAB_SWIPE_MAX_DURATION_MS = 1000;
const TAB_SWIPE_EDGE_GUARD_PX = 24;

function firstQueryValue(value) {
    return Array.isArray(value) ? value[0] : value;
}

function normalizeRequestTypeRouteFilter(value) {
    const normalized = String(firstQueryValue(value) || '').trim();
    if (!normalized) {
        return null;
    }

    if (/^[1-9]\d*$/.test(normalized)) {
        return normalized;
    }

    if (normalized.indexOf(REQUEST_CATEGORY_FILTER_PREFIX) === 0) {
        const category = normalized.slice(REQUEST_CATEGORY_FILTER_PREFIX.length);
        if (REQUEST_CATEGORIES.some(option => option.value === category)) {
            return `${REQUEST_CATEGORY_FILTER_PREFIX}${category}`;
        }
    }

    return null;
}

export default {
    name: "Requests",
    components: {
        RequestsNavbar,
        RequestShortItem
    },
    data() {
        return {
            highlightCleanupTimer: null,
            scrollHeightRaf: null,
            autoRefreshInProgress: false,
            routeFilterSyncing: false,
            tabSwipeStart: null,
            showTabSwipeHint: false,
            tabSwipeHintTimer: null
        };
    },
    computed: {
        selectedTab: {
            get() { return this.$store.state.requests.selectedTab; },
            set(val) { this.$store.commit('requests_setSelectedTab', val); }
        },
        sortBy() {
            return this.$store.state.requests.sortBy;
        },
        sortDesc() {
            return this.$store.state.requests.sortDesc;
        },
        filter() {
            return this.$store.state.requests.filter;
        },
        statusFilter() {
            return this.$store.state.requests.statusFilter;
        },
        requestTypeFilter() {
            return this.$store.state.requests.requestTypeFilter;
        },
        buildingProcessFilter() {
            return this.$store.state.requests.buildingProcessFilter;
        },
        sortOptions() {
            return this.$store.state.requests.sortOptions;
        },
        onProcess() {
            return this.$store.state.requests.loading;
        },
        allRequests() {
            return this.$store.getters.requests_allRequests;
        },
        counts() {
            return this.$store.getters.requests_counts;
        },
        listTotal() {
            return this.$store.getters.requests_listTotal;
        },
        listPage() {
            return this.$store.getters.requests_listPage;
        },
        listPerPage() {
            return this.$store.getters.requests_listPerPage;
        },
        currentPage: {
            get() { return this.listPage; },
            set(val) { this.$store.commit('requests_setListPage', val); }
        },
        requestTypes() {
            return this.$store.state.requests.requestTypes;
        },
        requestTypeFilterOptions() {
            return this.$store.state.requests.requestTypeFilterOptions;
        },
        buildingProcessFilterOptions() {
            return this.$store.state.requests.buildingProcessFilterOptions;
        },
        badgeFilterOptions() {
            return this.$store.state.requests.badgeFilterOptions || [];
        },
        canCreateRequest() {
            return Boolean(this.requestTypes.length);
        },
        isTelegramWebApps() {
            return this.$store.getters.isTelegramWebApps;
        },
        isMobile() {
            return this.$store.getters.isMobile;
        },
        isMobileBrowser() {
            return this.isMobile && !this.isTelegramWebApps;
        },
        isAdmin() {
            return this.$store.getters.isAdmin;
        },
        tabNames() {
            return this.isAdmin ? ADMIN_TAB_NAMES : BASE_TAB_NAMES;
        }
    },
    methods: {
        changeRecurrenceFilter(value) {
            this.$store.commit('requests_setRecurrenceFilter', value);
            this.fetchRequests({resetPage: true});
        },
        showTabSwipeHintOnce() {
            if (!this.isMobile) return;

            try {
                if (localStorage.getItem(TAB_SWIPE_HINT_STORAGE_KEY)) return;
                localStorage.setItem(TAB_SWIPE_HINT_STORAGE_KEY, '1');
            } catch (e) { /* localStorage может быть недоступен в приватном WebView */ }

            this.showTabSwipeHint = true;
            this.clearTabSwipeHintTimer();
            this.tabSwipeHintTimer = setTimeout(() => {
                if (this._isDestroyed) return;
                this.showTabSwipeHint = false;
                this.tabSwipeHintTimer = null;
            }, TAB_SWIPE_HINT_DURATION_MS);
        },
        clearTabSwipeHintTimer() {
            if (!this.tabSwipeHintTimer) return;
            clearTimeout(this.tabSwipeHintTimer);
            this.tabSwipeHintTimer = null;
        },
        dismissTabSwipeHint() {
            this.showTabSwipeHint = false;
            this.clearTabSwipeHintTimer();
        },
        onTabSwipeStart(event) {
            this.resetTabSwipe();
            if (
                !this.isMobile ||
                !event.touches ||
                event.touches.length !== 1 ||
                !event.target ||
                typeof event.target.closest !== 'function' ||
                !event.target.closest('.requests-scroll-body')
            ) {
                return;
            }

            const touch = event.touches[0];
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            if (
                touch.clientX <= TAB_SWIPE_EDGE_GUARD_PX ||
                touch.clientX >= viewportWidth - TAB_SWIPE_EDGE_GUARD_PX
            ) {
                return;
            }

            this.tabSwipeStart = {
                x: touch.clientX,
                y: touch.clientY,
                startedAt: Date.now()
            };
        },
        onTabSwipeEnd(event) {
            const start = this.tabSwipeStart;
            this.resetTabSwipe();
            if (
                !start ||
                !event.changedTouches ||
                event.changedTouches.length !== 1 ||
                event.touches && event.touches.length
            ) {
                return;
            }

            const touch = event.changedTouches[0];
            const deltaX = touch.clientX - start.x;
            const deltaY = touch.clientY - start.y;
            const absoluteX = Math.abs(deltaX);
            const absoluteY = Math.abs(deltaY);
            const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
            const swipeThreshold = Math.min(
                TAB_SWIPE_MAX_DISTANCE_PX,
                Math.max(TAB_SWIPE_MIN_DISTANCE_PX, viewportWidth * TAB_SWIPE_VIEWPORT_RATIO)
            );

            if (
                Date.now() - start.startedAt > TAB_SWIPE_MAX_DURATION_MS ||
                absoluteX < swipeThreshold ||
                absoluteX <= absoluteY * TAB_SWIPE_HORIZONTAL_RATIO
            ) {
                return;
            }

            const direction = deltaX < 0 ? 1 : -1;
            const nextTab = Math.max(0, Math.min(this.selectedTab + direction, this.tabNames.length - 1));
            if (nextTab === this.selectedTab) return;

            this.dismissTabSwipeHint();
            this.onTabChange(nextTab);
        },
        resetTabSwipe() {
            this.tabSwipeStart = null;
        },
        currentTabName() {
            return this.tabNames[this.selectedTab] || 'all';
        },
        normalizeSelectedTab() {
            const maxIndex = this.tabNames.length - 1;
            if (this.selectedTab > maxIndex || this.selectedTab < 0) {
                this.$store.commit('requests_setSelectedTab', Math.max(0, Math.min(this.selectedTab, maxIndex)));
            }
        },
        applyRouteFilters(query = {}, previousQuery = null) {
            let changed = false;
            const routeTab = String(firstQueryValue(query.tab) || '').trim();
            const routeTabIndex = this.tabNames.indexOf(routeTab);
            if (routeTabIndex >= 0 && routeTabIndex !== this.selectedTab) {
                this.routeFilterSyncing = true;
                this.$store.commit('requests_setSelectedTab', routeTabIndex);
                this.$nextTick(() => {
                    this.routeFilterSyncing = false;
                });
                changed = true;
            } else if (
                !routeTab &&
                previousQuery &&
                Object.prototype.hasOwnProperty.call(previousQuery, 'tab') &&
                this.selectedTab !== 0
            ) {
                this.routeFilterSyncing = true;
                this.$store.commit('requests_setSelectedTab', 0);
                this.$nextTick(() => {
                    this.routeFilterSyncing = false;
                });
                changed = true;
            }

            const hasRequestTypeFilter = Object.prototype.hasOwnProperty.call(query, 'request_type_id');
            const previouslyHadRequestTypeFilter = previousQuery &&
                Object.prototype.hasOwnProperty.call(previousQuery, 'request_type_id');
            if (hasRequestTypeFilter) {
                const routeRequestType = normalizeRequestTypeRouteFilter(query.request_type_id) || 'all';
                if (String(this.requestTypeFilter || 'all') !== String(routeRequestType)) {
                    this.$store.commit('requests_setRequestTypeFilter', routeRequestType);
                    changed = true;
                }
            } else if (previouslyHadRequestTypeFilter && String(this.requestTypeFilter || 'all') !== 'all') {
                this.$store.commit('requests_setRequestTypeFilter', 'all');
                changed = true;
            }

            return changed;
        },
        replaceRouteQueryValue(key, value) {
            if (!Object.prototype.hasOwnProperty.call(this.$route.query || {}, key)) {
                return;
            }

            const query = {...this.$route.query};
            if (value === undefined || value === null || value === '' || value === 'all') {
                delete query[key];
            } else {
                query[key] = String(value);
            }
            this.$router.replace({
                path: this.$route.path,
                query
            }).catch(() => {});
        },
        async fetchRequests(opts = {}) {
            try {
                this.normalizeSelectedTab();
                await this.$store.dispatch('requests_getRequests', {
                    tab: this.currentTabName(),
                    ...opts
                });
                this.scheduleUpdateScrollHeight();
            } catch (e) {
                this.$bvToast && this.$bvToast.toast('Не удалось загрузить заявки', {
                    title: 'Ошибка',
                    variant: 'danger',
                    solid: true
                });
            }
        },
        async onTabChange(tabIndex) {
            this.$store.commit('requests_setSelectedTab', tabIndex);
            if (this.routeFilterSyncing) {
                return;
            }
            this.replaceRouteQueryValue('tab', this.tabNames[tabIndex]);
            try {
                await this.$store.dispatch('requests_getCounts');
            } catch (e) {
                console.error('Ошибка при обновлении счетчиков:', e);
            }
            this.fetchRequests({ resetPage: true });
            this.scheduleUpdateScrollHeight();
        },
        onSortByChange(value) {
            this.$store.commit('requests_setSortBy', value);
            if (value === 'createdAt') {
                this.$store.commit('requests_setSortDesc', true);
            }
            this.fetchRequests({ resetPage: true });
        },
        onSortDescChange(value) {
            this.$store.commit('requests_setSortDesc', value);
            this.fetchRequests({ resetPage: true });
        },
        onFilterChange(value) {
            this.$store.commit('requests_setFilter', value);
            this.fetchRequests({ resetPage: true });
        },
        onStatusFilterChange(value) {
            this.$store.commit('requests_setStatusFilter', value);
            this.fetchRequests({ resetPage: true });
        },
        onRequestTypeFilterChange(value) {
            this.$store.commit('requests_setRequestTypeFilter', value);
            this.replaceRouteQueryValue('request_type_id', value);
            this.fetchRequests({ resetPage: true });
        },
        onBuildingProcessFilterChange(value) {
            this.$store.commit('requests_setBuildingProcessFilter', value);
            this.fetchRequests({ resetPage: true });
        },
        onFiltersReset() {
            this.$store.commit('requests_setRecurrenceFilter', '');
            this.$store.commit('requests_setFilter', '');
            this.$store.commit('requests_setStatusFilter', 'all');
            this.$store.commit('requests_setRequestTypeFilter', 'all');
            this.$store.commit('requests_setBuildingProcessFilter', 'all');
            this.replaceRouteQueryValue('request_type_id', null);
            this.fetchRequests({ resetPage: true });
        },
        onPageChange(page) {
            this.fetchRequests({ page });
        },
        scrollToElement(elementId) {
            const element = document.getElementById(elementId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        },
        async autoRefreshOnResume() {
            if (document.hidden || this.autoRefreshInProgress || this.onProcess) {
                return;
            }

            this.autoRefreshInProgress = true;
            try {
                await Promise.all([
                    this.$store.dispatch('requests_getCounts'),
                    this.$store.dispatch('requests_getRequestTypeFilterOptions'),
                    this.$store.dispatch('requests_getBuildingProcessFilterOptions'),
                    this.fetchRequests()
                ]);
            } catch (e) {
                console.error('Не удалось автообновить список заявок:', e);
            } finally {
                this.autoRefreshInProgress = false;
            }
        },
        handleVisibilityChange() {
            if (!document.hidden) {
                this.autoRefreshOnResume();
            }
        },
        resetDocumentScroll() {
            if (typeof window === 'undefined' || typeof document === 'undefined') return;

            window.scrollTo(0, 0);
            if (document.documentElement) {
                document.documentElement.scrollTop = 0;
            }
            if (document.body) {
                document.body.scrollTop = 0;
            }
        },
        handlePageShow() {
            this.resetDocumentScroll();
            this.scheduleUpdateScrollHeight();
        },
        getViewportBottom() {
            if (window.visualViewport && window.visualViewport.height) {
                return window.visualViewport.offsetTop + window.visualViewport.height;
            }
            return window.innerHeight || document.documentElement.clientHeight;
        },
        scheduleUpdateScrollHeight() {
            if (typeof window === 'undefined') return;
            if (this.scrollHeightRaf) {
                cancelAnimationFrame(this.scrollHeightRaf);
            }
            this.scrollHeightRaf = requestAnimationFrame(() => {
                this.scrollHeightRaf = null;
                this.updateScrollHeight();
            });
        },
        updateScrollHeight() {
            const activeScrollBody = this.$el && this.$el.querySelector('.tab-content .tab-pane.active .requests-scroll-body');
            if (!activeScrollBody) return;

            const pagination = this.$el.querySelector('.requests-pagination');
            const viewportBottom = this.getViewportBottom();
            const pageBottom = this.$el.getBoundingClientRect().bottom;
            const scrollTop = activeScrollBody.getBoundingClientRect().top;
            const paginationHeight = pagination ? pagination.getBoundingClientRect().height : 0;
            const bottomGap = 4;
            const height = Math.floor(Math.min(viewportBottom, pageBottom) - scrollTop - paginationHeight - bottomGap);

            this.$el.style.setProperty('--requests-scroll-height', `${Math.max(0, height)}px`);
        },
        /** Корень RequestShortItem — обёртка; видимая карточка — вложенный .card (inset-тень на обёртке скрыта под card). */
        resolveHighlightTarget(wrapperEl) {
            if (!wrapperEl) return null;
            if (wrapperEl.classList && wrapperEl.classList.contains('card')) {
                return wrapperEl;
            }
            const direct = wrapperEl.firstElementChild;
            if (direct && direct.classList && direct.classList.contains('card')) {
                return direct;
            }
            return wrapperEl.querySelector('.card') || wrapperEl;
        },
        applyHighlightBlink(wrapperEl) {
            const el = this.resolveHighlightTarget(wrapperEl);
            if (!el) return;
            const cls = 'requests-list-highlight-blink';
            el.classList.remove(cls);
            void el.offsetWidth;
            el.classList.add(cls);
            const onEnd = () => {
                el.classList.remove(cls);
                el.removeEventListener('animationend', onEnd);
            };
            el.addEventListener('animationend', onEnd);
        },
        scrollElementInsideList(wrapperEl, scrollBody) {
            if (!wrapperEl || !scrollBody) return;

            const bodyRect = scrollBody.getBoundingClientRect();
            const wrapperRect = wrapperEl.getBoundingClientRect();
            const targetTop = scrollBody.scrollTop
                + wrapperRect.top
                - bodyRect.top
                - ((scrollBody.clientHeight - wrapperRect.height) / 2);
            const top = Math.max(0, targetTop);

            if (typeof scrollBody.scrollTo === 'function') {
                scrollBody.scrollTo({ top, behavior: 'smooth' });
            } else {
                scrollBody.scrollTop = top;
            }
        },
        async scrollToHighlight(highlightId) {
            // Два тика: переключение b-tabs и рендер списка
            await this.$nextTick();
            await this.$nextTick();
            // На каждой вкладке дублируются id — ищем только в активной панели
            const pane = this.$el.querySelector('.tab-content .tab-pane.active');
            const scrollBody = pane && pane.querySelector('.requests-scroll-body');
            const wrapper = pane && pane.querySelector(`#id_${highlightId}`);
            if (wrapper) {
                if (scrollBody) {
                    this.scrollElementInsideList(wrapper, scrollBody);
                } else {
                    wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                this.resetDocumentScroll();
                // После smooth scroll мигание на карточке, иначе эффект уходит вне экрана или не попадает в поле зрения
                setTimeout(() => {
                    if (this._isDestroyed) return;
                    this.resetDocumentScroll();
                    this.applyHighlightBlink(wrapper);
                    this.scheduleUpdateScrollHeight();
                }, 550);
            }
        },
        readSession(highlightId) {
            try {
                const raw = sessionStorage.getItem(SESSION_KEY);
                if (!raw) return null;
                const data = JSON.parse(raw);
                if (
                    data.requestId === highlightId &&
                    Date.now() - data.savedAt < SESSION_TTL_MS
                ) {
                    return data;
                }
            } catch (e) { /* ignore */ }
            return null;
        },
        clearSession() {
            try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
        },
        clearHighlightCleanupTimer() {
            if (this.highlightCleanupTimer) {
                clearTimeout(this.highlightCleanupTimer);
                this.highlightCleanupTimer = null;
            }
        },
        scheduleHighlightCleanup(highlightId) {
            this.clearHighlightCleanupTimer();
            this.highlightCleanupTimer = setTimeout(() => {
                if (this._isDestroyed) return;
                // Чистим только служебный highlight, остальные query сохраняем.
                // Если highlight уже другой (новая навигация), ничего не трогаем.
                const currentHighlight = this.$route.query && this.$route.query.highlight;
                if (!currentHighlight || String(currentHighlight) !== String(highlightId)) {
                    return;
                }
                const nextQuery = { ...this.$route.query };
                delete nextQuery.highlight;
                this.$router.replace({
                    path: this.$route.path,
                    query: nextQuery
                }).catch(() => {});
            }, HIGHLIGHT_CLEANUP_DELAY_MS);
        },
        async restoreFromHighlight(highlightId) {
            const id = parseInt(highlightId, 10);
            if (!id) return;

            const session = this.readSession(id);

            if (session) {
                // Быстрый путь: контекст сохранён в sessionStorage
                this.clearSession();
                const rawTabIndex = Number(session.tab);
                const tabIndex = Number.isInteger(rawTabIndex)
                    ? Math.max(0, Math.min(rawTabIndex, this.tabNames.length - 1))
                    : 0;
                this.$store.commit('requests_setSelectedTab', tabIndex);
                this.$store.commit('requests_setListPage', session.page);
                await this.fetchRequests();
                await this.scrollToHighlight(id);
            } else {
                // Медленный путь: запрашиваем у сервера
                try {
                    const ctx = await requestsApi.getRequestListContext(id, {
                        per_page: this.listPerPage,
                        sort_by: this.sortBy,
                        sort_desc: this.sortDesc,
                        q: this.filter,
                        status: this.statusFilter,
                        request_type_id: this.requestTypeFilter,
                        building_process_id: this.buildingProcessFilter
                    });
                    if (ctx.found) {
                        const tabIndex = this.tabNames.indexOf(ctx.tab);
                        this.$store.commit('requests_setSelectedTab', tabIndex >= 0 ? tabIndex : 2);
                        this.$store.commit('requests_setListPage', ctx.page);
                    }
                } catch (e) {
                    console.error('Не удалось получить контекст списка:', e);
                }
                await this.fetchRequests();
                await this.scrollToHighlight(id);
            }

            // Не удаляем highlight мгновенно: это ломает часть history-сценариев.
            // Удаляем с задержкой и только сам highlight, сохраняя остальные query.
            this.scheduleHighlightCleanup(id);
        }
    },
    watch: {
        '$route.query': {
            handler(query, previousQuery) {
                if (!this.applyRouteFilters(query, previousQuery)) {
                    return;
                }
                this.$store.dispatch('requests_getCounts').catch(error => {
                    console.error('Ошибка при обновлении счетчиков:', error);
                });
                this.fetchRequests({resetPage: true});
            }
        }
    },
    created() {
        // Выбираем вкладку до создания b-tabs: иначе его начальное значение
        // успевает перезаписать вкладку, указанную в ссылке на рабочую очередь.
        this.normalizeSelectedTab();
        this.applyRouteFilters(this.$route.query);
    },
    async mounted() {
        this.resetDocumentScroll();
        this.showTabSwipeHintOnce();
        try {
            await Promise.all([
                this.$store.dispatch('requests_getCounts'),
                this.$store.dispatch('requests_getRequestTypes'),
                this.$store.dispatch('requests_getRequestTypeFilterOptions'),
                this.$store.dispatch('requests_getBuildingProcessFilterOptions')
            ]);
        } catch (e) {
            console.error('Ошибка при загрузке начальных данных:', e);
        }

        const highlight = this.$route.query.highlight;
        if (highlight) {
            await this.restoreFromHighlight(highlight);
        } else {
            this.fetchRequests();
        }

        window.addEventListener('resize', this.scheduleUpdateScrollHeight);
        window.addEventListener('focus', this.autoRefreshOnResume);
        window.addEventListener('pageshow', this.handlePageShow);
        window.addEventListener('orientationchange', this.scheduleUpdateScrollHeight);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', this.scheduleUpdateScrollHeight);
        }
        this.resetDocumentScroll();
        this.scheduleUpdateScrollHeight();
    },
    updated() {
        this.scheduleUpdateScrollHeight();
    },
    beforeDestroy() {
        this.clearHighlightCleanupTimer();
        this.clearTabSwipeHintTimer();
        this.resetTabSwipe();
        if (this.scrollHeightRaf) {
            cancelAnimationFrame(this.scrollHeightRaf);
            this.scrollHeightRaf = null;
        }
        window.removeEventListener('resize', this.scheduleUpdateScrollHeight);
        window.removeEventListener('focus', this.autoRefreshOnResume);
        window.removeEventListener('pageshow', this.handlePageShow);
        window.removeEventListener('orientationchange', this.scheduleUpdateScrollHeight);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        if (window.visualViewport) {
            window.visualViewport.removeEventListener('resize', this.scheduleUpdateScrollHeight);
        }
    }
}
</script>

<style scoped>
.requests-page {
    --requests-page-offset: 48px;
    --requests-scroll-height: 60vh;
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    height: calc(100vh - var(--requests-page-offset));
    height: calc(100dvh - var(--requests-page-offset));
}

.requests-page--mobile-layout {
    --requests-page-offset: 48px;
}

.requests-page--telegram {
    --requests-page-offset: 48px;
}

.requests-list-card {
    position: relative;
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}

.requests-swipe-hint {
    position: fixed;
    z-index: 1045;
    right: 12px;
    bottom: calc(82px + env(safe-area-inset-bottom, 0px));
    left: 12px;
    display: flex;
    align-items: center;
    gap: .45rem;
    width: calc(100vw - 24px);
    max-width: 28rem;
    margin: 0 auto;
    padding: .4rem .45rem;
    border: 1px solid #9ec5fe;
    border-radius: .35rem;
    background: #e7f1ff;
    box-shadow: 0 8px 24px rgba(34, 75, 126, .2);
    color: #084298;
    font-size: .8rem;
    line-height: 1.25;
}

.requests-swipe-hint__icon {
    flex: 0 0 auto;
    font-size: 1rem;
}

.requests-swipe-hint__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 28px;
    height: 28px;
    margin: -.25rem -.25rem -.25rem auto;
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
}

.requests-swipe-hint-enter-active,
.requests-swipe-hint-leave-active {
    transition: opacity .18s ease, transform .18s ease;
}

.requests-swipe-hint-enter,
.requests-swipe-hint-leave-to {
    opacity: 0;
    transform: translateY(-4px);
}

.requests-tabs {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}

.requests-page :deep(.tabs > .card-body) {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
}

.requests-page :deep(.tab-content) {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
}

.requests-page :deep(.tab-content > .tab-pane) {
    min-height: 0;
}

.requests-page :deep(.tab-content > .active) {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
}

.requests-scroll-body {
    flex: 0 1 auto;
    height: var(--requests-scroll-height);
    min-height: 0;
    max-height: var(--requests-scroll-height);
    box-sizing: border-box;
    padding-bottom: .25rem;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
}

@media (max-width: 767.98px) {
    .requests-scroll-body {
        min-height: 0;
    }
}

/* ─── таб-навигация: горизонтальный скролл на мобиле ─── */
@media (max-width: 767.98px) {
    .requests-page :deep(.nav-tabs) {
        flex-wrap: nowrap !important;
        overflow-x: auto;
        overflow-y: hidden;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        padding-bottom: 1px; /* не срезать нижнюю рамку активного таба */
    }

    .requests-page :deep(.nav-tabs::-webkit-scrollbar) {
        display: none;
    }

    .requests-page :deep(.nav-tabs .nav-item) {
        flex-shrink: 0;
    }

    .requests-page :deep(.nav-tabs .nav-link) {
        padding: .35rem .6rem;
        font-size: .875rem;
        white-space: nowrap;
    }

    /* меньше padding у контентной зоны таба на мобиле */
    .requests-page :deep(.tab-content > .tab-pane) {
        padding: .5rem .5rem;
    }

    /* уменьшить отступ card под табами */
    .requests-page :deep(.card-header) {
        padding: .15rem .75rem .5rem;
    }
}

/* ─── пагинация: safe-area для Telegram / нотч ─── */
.requests-pagination {
    flex: 0 0 auto;
    padding-bottom: calc(.25rem + env(safe-area-inset-bottom, 0px)) !important;
}

.requests-pagination :deep(.pagination) {
    margin-bottom: 0;
}
</style>

<!-- класс вешается на корень RequestShortItem из JS; scoped не пробивает дочерний корень -->
<style>
@keyframes requests-list-highlight-blink-anim {
    0%,
    100% {
        box-shadow: none;
        filter: none;
    }
    50% {
        box-shadow:
            0 0 0 3px rgba(13, 110, 253, 0.95),
            0 0 22px 4px rgba(13, 110, 253, 0.35);
        filter: brightness(0.97);
    }
}

.requests-list-highlight-blink {
    position: relative;
    z-index: 2;
    animation: requests-list-highlight-blink-anim 0.32s ease-in-out 5;
    border-radius: 0.25rem;
}

@media (max-width: 767.98px) {
    .requests-page .requests-tabs > .card-header {
        padding: .15rem .75rem .5rem !important;
        overflow: hidden;
    }

    .requests-page .requests-tabs > .card-header .nav-tabs {
        flex-wrap: nowrap !important;
        overflow-x: auto;
        overflow-y: hidden;
        padding-bottom: 1px;
        margin-bottom: 0;
        white-space: nowrap;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
    }

    .requests-page .requests-tabs > .card-header .nav-tabs::-webkit-scrollbar {
        display: none;
    }

    .requests-page .requests-tabs > .card-header .nav-tabs .nav-item {
        flex: 0 0 auto;
        margin-bottom: 0;
    }

    .requests-page .requests-tabs > .card-header .nav-tabs .nav-link {
        padding: .35rem .6rem;
        font-size: .875rem;
        white-space: nowrap;
    }
}
</style>
